import React, { useMemo } from 'react';
import { Dropdown, Input, Spin, Tooltip } from 'antd';
import { MoreOutlined, ReloadOutlined, SearchOutlined, StarFilled } from '@ant-design/icons';
import { ChatAvatar } from '../../../components/chat';
import { useTranslation } from '../../../i18n';
import type { Conversation, InboxTab } from '../../../services/messageService';
import { formatInboxTime } from '../../../utils/timeFormat';

type InboxListProps = {
  conversations: Conversation[];
  activeId?: string;
  tab: InboxTab;
  tabCounts: Record<InboxTab, number>;
  keyword: string;
  loading: boolean;
  onTabChange: (tab: InboxTab) => void;
  onKeywordChange: (keyword: string) => void;
  onSelect: (id: string) => void;
  onRefresh: () => void;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  onMarkAllRead?: () => void;
};

export function InboxList({
  conversations,
  activeId,
  tab,
  tabCounts,
  keyword,
  loading,
  onTabChange,
  onKeywordChange,
  onSelect,
  onRefresh,
  onLoadMore,
  loadingMore,
  onMarkAllRead,
}: InboxListProps) {
  const { t } = useTranslation();
  const tabs = useMemo(
    () => [
      { key: 'all' as const, label: t('message.filter_all') },
      { key: 'unread' as const, label: t('message.unread'), showCount: true },
      { key: 'unreplied' as const, label: t('message.unreplied'), showCount: true },
      { key: 'starred' as const, label: t('message.star') },
      { key: 'archived' as const, label: t('message.archived') },
    ],
    [t],
  );
  const menuItems = useMemo(
    () => [{ key: 'readAll', label: t('message.mark_all_read'), disabled: !onMarkAllRead }],
    [t, onMarkAllRead],
  );

  return (
    <section className="flex h-full w-[340px] shrink-0 flex-col border-r border-[#eef0f4] bg-white">
      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <span className="text-[15px] font-semibold text-slate-800">{t('message.inbox')}</span>
        <div className="flex items-center gap-1 text-slate-400">
          <Tooltip title={t('message.refresh')}>
            <button
              type="button"
              className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-slate-400 hover:bg-slate-100 hover:text-[#596ae8]"
              onClick={onRefresh}
              aria-label={t('message.refresh')}
            >
              <ReloadOutlined spin={loading} />
            </button>
          </Tooltip>
          <Dropdown
            trigger={['click']}
            menu={{
              items: menuItems,
              onClick: ({ key }) => {
                if (key === 'readAll') {
                  onMarkAllRead?.();
                }
              },
            }}
          >
            <button
              type="button"
              className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-slate-400 hover:bg-slate-100 hover:text-[#596ae8]"
              aria-label={t('message.more')}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      </div>

      <div className="px-4 pb-2">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-slate-400" />}
          placeholder={t('message.search_placeholder')}
          value={keyword}
          onChange={(e) => onKeywordChange(e.target.value)}
          className="rounded-lg"
        />
      </div>

      <div
        className="flex items-center justify-between border-b border-[#eef0f4] px-3"
        role="tablist"
      >
        {tabs.map((item) => {
          const active = item.key === tab;
          const count = tabCounts[item.key];
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`-mb-px inline-flex cursor-pointer items-center gap-1 whitespace-nowrap border-0 border-b-2 bg-transparent px-1.5 py-2 text-[13px] transition-colors ${
                active
                  ? 'border-[#596ae8] font-semibold text-[#3b4dd7]'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => onTabChange(item.key)}
            >
              {item.label}
              {item.showCount && count > 0 ? (
                <span className="min-w-[16px] rounded-full bg-[#596ae8] px-1 text-center text-[10px] leading-4 text-white">
                  {count > 99 ? '99+' : count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        className="chat-scroll min-h-0 flex-1 overflow-y-auto"
        onScroll={(event) => {
          if (!onLoadMore) return;
          const el = event.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) onLoadMore();
        }}
      >
        {loading && conversations.length === 0 ? (
          <div className="flex justify-center py-10">
            <Spin size="small" />
          </div>
        ) : null}
        {!loading && conversations.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400">
            {keyword.trim() ? t('message.no_match') : t('message.empty_inbox')}
          </div>
        ) : null}
        {conversations.map((item) => {
          const active = item.id === activeId;
          return (
            <button
              key={item.id}
              type="button"
              className={`relative flex w-full cursor-pointer items-start gap-3 border-0 border-b border-[#f1f3f7] px-4 py-3 text-left transition-colors ${active ? 'bg-[#596ae8]/[0.07]' : 'bg-white hover:bg-slate-50'
                }`}
              onClick={() => onSelect(item.id)}
            >
              {active ? (
                <span className="absolute inset-y-0 left-0 w-[3px] bg-[#596ae8]" aria-hidden />
              ) : null}
              <span className="relative">
                <ChatAvatar participant={item.peer} size={36} rounded="lg" />
                {item.peer.online ? (
                  <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-white bg-emerald-500" />
                ) : null}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="truncate text-sm font-semibold text-slate-800">{item.peer.name}</span>
                    {item.starred ? (
                      <StarFilled className="shrink-0 text-[11px] text-amber-400" aria-label={t('message.star')} />
                    ) : null}
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">
                    {item.lastMessageAt ? formatInboxTime(item.lastMessageAt) : ''}
                  </span>
                </span>
                <span className="mt-0.5 flex items-center justify-between gap-2">
                  <span
                    className={`truncate text-xs ${item.unreadCount > 0 ? 'text-slate-600' : 'text-slate-400'
                      }`}
                  >
                    {item.lastMessageDirection === 'out' ? t('chat.me_prefix') : ''}
                    {item.lastMessagePreview}
                  </span>
                  {item.unreadCount > 1 ? (
                    <span className="min-w-[18px] shrink-0 rounded-full bg-[#596ae8] px-1 text-center text-[10px] font-semibold leading-[18px] text-white">
                      {item.unreadCount > 99 ? '99+' : item.unreadCount}
                    </span>
                  ) : item.unreadCount === 1 ? (
                    <span className="size-2 shrink-0 rounded-full bg-[#596ae8]" aria-label={t('message.unread')} />
                  ) : null}
                </span>
              </span>
            </button>
          );
        })}
        {loadingMore ? (
          <div className="flex justify-center py-3">
            <Spin size="small" />
          </div>
        ) : null}
      </div>
    </section>
  );
}
